import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileDown, TrendingUp, Sparkles, Quote, Lightbulb, Copy, Check } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { CJ_AVATAR_URL } from "../lib/constants";
import { toast } from "sonner";

const BAR_COLOR_BY_INDEX = ["#7ac143", "#0091be", "#f68b1f", "#ffd400", "#ef4444", "#083050"];

export default function ImpactDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/impact/reports/${id}`).then(({ data }) => setR(data)).catch(() => setR(null));
  }, [id]);

  const copyPitch = async () => {
    if (!r?.elevator_pitch) return;
    try {
      await navigator.clipboard.writeText(r.elevator_pitch);
      setCopied(true);
      toast.success("Pitch copied, paste it anywhere.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  if (!r) return <div className="p-10 text-center text-slate-400">Loading…</div>;

  const overall = r.overall || {};
  const pct = overall.percent_improvement;
  const pre = overall.avg_pre_score;
  const post = overall.avg_post_score;
  const scaleMax = overall.scale_max || 5;
  const maxCatPct = Math.max(...(r.categories || []).map((c) => c.percent_improvement || 0), 100);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-16">
      <Link to="/impact" data-testid="impact-detail-back" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink">
        <ArrowLeft size={14} /> All Reports
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        className="tc-card mt-4 p-6 sm:p-8 relative overflow-hidden"
        style={{ backgroundImage: "linear-gradient(135deg,#FFFFFF 0%,#F0FAE5 100%)" }}
        data-testid="impact-detail-card"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <img src={CJ_AVATAR_URL} alt="CJ" className="h-14 w-14 rounded-full object-cover shadow-sm ring-2 ring-white shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <div className="tc-eyebrow text-green-700">Impact Report</div>
            <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight mt-1">{r.client_name || "Client report"}</h1>
            <p className="text-slate-600 mt-1.5">{r.program_name}{r.report_date ? ` · ${r.report_date}` : ""}{r.participant_count ? ` · ${r.participant_count} participants` : ""}</p>
          </div>
          {r.pdf_path && (
            <a
              href={fileUrl(r.pdf_path)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="impact-pdf-download"
              className="h-11 px-4 rounded-2xl bg-white border border-slate-200 text-tc-ink font-bold text-sm flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all"
            >
              <FileDown size={16} /> Original PDF
            </a>
          )}
        </div>
        {r.summary && (
          <p className="mt-6 text-slate-700 text-base sm:text-lg leading-relaxed border-l-4 border-green-400 pl-4 italic" data-testid="impact-summary">
            {r.summary}
          </p>
        )}
      </motion.div>

      {/* Elevator pitch, ready to paste into outreach */}
      {r.elevator_pitch && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, type: "spring", stiffness: 140, damping: 18 }}
          className="tc-card p-6 sm:p-7 mt-4 relative overflow-hidden"
          style={{ backgroundImage: "linear-gradient(135deg,#FFFFFF 0%,#FFF7EE 100%)" }}
          data-testid="impact-elevator-pitch"
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="tc-eyebrow text-orange-600 flex items-center gap-1.5"><Sparkles size={12} /> Ready-to-paste pitch</div>
              <h2 className="font-display text-xl text-tc-ink mt-1 leading-snug">Drop this in your next outreach email.</h2>
            </div>
            <button
              onClick={copyPitch}
              data-testid="impact-pitch-copy"
              className="h-10 px-4 rounded-xl text-white font-bold text-sm flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all"
              style={{ background: copied ? "#7ac143" : "#f68b1f" }}
            >
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy pitch</>}
            </button>
          </div>
          <p className="mt-4 text-slate-800 text-base leading-relaxed whitespace-pre-line" data-testid="impact-pitch-text">{r.elevator_pitch}</p>
        </motion.div>
      )}

      {/* Overall snapshot */}
      {(typeof pct === "number" || typeof pre === "number") && (
        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mt-6">
          <Stat label="Avg Pre Score" value={pre != null ? `${pre.toFixed(2)}` : ","} sub={pre != null ? `/ ${scaleMax}` : ""} color="#94A3B8" testid="impact-pre" />
          <Stat label="Avg Post Score" value={post != null ? `${post.toFixed(2)}` : ","} sub={post != null ? `/ ${scaleMax}` : ""} color="#0091be" testid="impact-post" />
          <Stat label="Overall Improvement" value={pct != null ? `${pct.toFixed(1)}%` : ","} color="#7ac143" testid="impact-pct" trend />
        </div>
      )}

      {/* Category bars */}
      {r.categories?.length > 0 && (
        <section className="tc-card p-6 sm:p-8 mt-6" data-testid="impact-categories">
          <h2 className="font-display text-2xl text-tc-ink leading-tight flex items-center gap-2">
            <Sparkles size={20} className="text-orange-500" /> Top Areas of Growth
          </h2>
          <div className="mt-5 space-y-4">
            {r.categories.map((c, i) => {
              const v = c.percent_improvement || 0;
              const width = Math.max(6, Math.min(100, (v / maxCatPct) * 100));
              const color = BAR_COLOR_BY_INDEX[i % BAR_COLOR_BY_INDEX.length];
              return (
                <div key={c.name} data-testid={`impact-cat-${c.name?.toLowerCase().replace(/\s+/g,"-")}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="font-bold text-tc-ink">{c.name}</span>
                    <span className="font-display text-2xl" style={{ color }}>{v}%</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${width}%` }}
                      transition={{ type: "spring", stiffness: 110, damping: 22, delay: 0.05 * i }}
                      className="h-full rounded-full"
                      style={{ background: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Areas to reinforce */}
      {r.areas_to_reinforce?.length > 0 && (
        <section className="tc-card p-6 sm:p-8 mt-6" data-testid="impact-reinforce">
          <h2 className="font-display text-2xl text-tc-ink flex items-center gap-2"><Lightbulb size={20} className="text-yellow-500" /> Areas to Reinforce</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            {r.areas_to_reinforce.map((a) => (
              <span key={a} className="tc-pill bg-yellow-50 text-yellow-800 border border-yellow-200">{a}</span>
            ))}
          </div>
        </section>
      )}

      {/* Participant Reflections */}
      {r.participant_reflections?.length > 0 && (
        <section className="tc-card p-6 sm:p-8 mt-6" data-testid="impact-reflections">
          <h2 className="font-display text-2xl text-tc-ink flex items-center gap-2"><Quote size={20} className="text-blue-500" /> Participant Reflections</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {r.participant_reflections.map((q, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-700 italic leading-relaxed">"{q}"</div>
            ))}
          </div>
        </section>
      )}

      {/* Leader actions */}
      {r.leader_actions?.length > 0 && (
        <section className="tc-card p-6 sm:p-8 mt-6" data-testid="impact-actions">
          <h2 className="font-display text-2xl text-tc-ink flex items-center gap-2"><TrendingUp size={20} className="text-green-600" /> Recommended Next Steps</h2>
          <ul className="mt-4 space-y-2.5">
            {r.leader_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2.5 text-slate-700 leading-relaxed">
                <span className="mt-1 w-2 h-2 rounded-full bg-green-500 shrink-0" /> <span>{a}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, sub, color, testid, trend }) {
  return (
    <div className="tc-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="tc-eyebrow text-slate-400">{label}</div>
        {trend && <TrendingUp size={14} className="text-green-500" />}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <div className="font-display text-4xl" style={{ color }}>{value}</div>
        {sub && <div className="text-sm text-slate-400 font-bold">{sub}</div>}
      </div>
    </div>
  );
}
